import { baserowService } from './baserow'

export interface ContentBlock {
  id: number
  key: string // Identifiant unique (ex: "hero_title", "hero_subtitle", etc.)
  type: 'text' | 'image' | 'rich_text' | 'number' | 'url' | 'color'
  value: string
  defaultValue: string
  description: string
  category: string // Groupement logique (ex: "hero", "features", "stats", etc.)
  page: string // Page concernée (ex: "home", "members", "venues", etc.)
  order: number // Ordre d'affichage
  lastUpdated: string
  updatedBy: string
}

export interface ContentCategory {
  name: string
  description: string
  blocks: ContentBlock[]
}

class ContentService {
  private readonly TABLE_ID = '811' // Table Baserow pour le contenu dynamique
  private cache: Map<string, ContentBlock> = new Map()
  private lastCacheUpdate: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  // Récupérer tout le contenu
  async getContent(): Promise<ContentBlock[]> {
    try {
      // Vérifier le cache
      if (this.isCacheValid()) {
        return Array.from(this.cache.values())
      }

      const response = await baserowService.getRecords(this.TABLE_ID, {}, 1, 200)
      const content = (response.results || response).map((item: any) => this.mapContentFromBaserow(item))
      
      // Mettre à jour le cache
      this.updateCache(content)
      
      return content
    } catch (error) {
      console.error('Error fetching content:', error)
      return Array.from(this.cache.values()) // Retourner le cache en cas d'erreur
    }
  }

  // Récupérer le contenu par clé
  async getContentByKey(key: string): Promise<ContentBlock | null> {
    const content = await this.getContent()
    return content.find(block => block.key === key) || null
  }

  // Récupérer le contenu par page
  async getContentByPage(page: string): Promise<ContentBlock[]> {
    const content = await this.getContent()
    return content.filter(block => block.page === page).sort((a, b) => a.order - b.order)
  }

  // Récupérer le contenu par catégorie
  async getContentByCategory(category: string): Promise<ContentBlock[]> {
    const content = await this.getContent()
    return content.filter(block => block.category === category).sort((a, b) => a.order - b.order)
  }

  // Mettre à jour un bloc de contenu
  async updateContent(id: number, updates: Partial<ContentBlock>): Promise<ContentBlock> {
    try {
      const baserowData: any = {}
      
      if (updates.value !== undefined) baserowData.field_7961 = updates.value
      if (updates.description !== undefined) baserowData.field_7963 = updates.description
      if (updates.category !== undefined) baserowData.field_7964 = updates.category
      if (updates.page !== undefined) baserowData.field_7965 = updates.page
      if (updates.order !== undefined) baserowData.field_7966 = updates.order
      
      // Ajouter les métadonnées de mise à jour
      baserowData.field_7967 = new Date().toISOString() // lastUpdated
      baserowData.field_7968 = 'admin' // updatedBy (à personnaliser selon l'utilisateur)

      const response = await baserowService.updateRecord(this.TABLE_ID, id.toString(), baserowData)
      const updatedContent = this.mapContentFromBaserow(response)
      
      // Mettre à jour le cache
      this.cache.set(updatedContent.key, updatedContent)
      
      return updatedContent
    } catch (error) {
      console.error('Error updating content:', error)
      throw error
    }
  }

  // Créer un nouveau bloc de contenu
  async createContent(data: Omit<ContentBlock, 'id' | 'lastUpdated' | 'updatedBy'>): Promise<ContentBlock> {
    try {
      const baserowData = {
        field_7958: data.key, // key
        field_7959: data.type, // type
        field_7961: data.value, // value
        field_7962: data.defaultValue, // defaultValue
        field_7963: data.description, // description
        field_7964: data.category, // category
        field_7965: data.page, // page
        field_7966: data.order, // order
        field_7967: new Date().toISOString(), // lastUpdated
        field_7968: 'admin' // updatedBy
      }

      const response = await baserowService.createRecord(this.TABLE_ID, baserowData)
      const newContent = this.mapContentFromBaserow(response)
      
      // Mettre à jour le cache
      this.cache.set(newContent.key, newContent)
      
      return newContent
    } catch (error) {
      console.error('Error creating content:', error)
      throw error
    }
  }

  // Supprimer un bloc de contenu
  async deleteContent(id: number): Promise<void> {
    try {
      await baserowService.deleteRecord(this.TABLE_ID, id.toString())
      
      // Supprimer du cache
      const contentToDelete = Array.from(this.cache.values()).find(c => c.id === id)
      if (contentToDelete) {
        this.cache.delete(contentToDelete.key)
      }
    } catch (error) {
      console.error('Error deleting content:', error)
      throw error
    }
  }

  // Récupérer le contenu organisé par catégories pour l'interface d'admin
  async getContentForAdmin(): Promise<ContentCategory[]> {
    const content = await this.getContent()
    const categories = new Map<string, ContentCategory>()

    content.forEach(block => {
      if (!categories.has(block.category)) {
        categories.set(block.category, {
          name: block.category,
          description: this.getCategoryDescription(block.category),
          blocks: []
        })
      }
      categories.get(block.category)!.blocks.push(block)
    })

    // Trier les blocs dans chaque catégorie
    categories.forEach(category => {
      category.blocks.sort((a, b) => a.order - b.order)
    })

    return Array.from(categories.values())
  }

  // Helper: Mapper les données Baserow vers ContentBlock
  private mapContentFromBaserow(item: any): ContentBlock {
    return {
      id: item.id,
      key: item.field_7958 || '',
      type: item.field_7959 || 'text',
      value: item.field_7961 || '',
      defaultValue: item.field_7962 || '',
      description: item.field_7963 || '',
      category: item.field_7964 || '',
      page: item.field_7965 || '',
      order: item.field_7966 || 0,
      lastUpdated: item.field_7967 || '',
      updatedBy: item.field_7968 || ''
    }
  }

  // Helper: Vérifier la validité du cache
  private isCacheValid(): boolean {
    return this.cache.size > 0 && (Date.now() - this.lastCacheUpdate) < this.CACHE_DURATION
  }

  // Helper: Mettre à jour le cache
  private updateCache(content: ContentBlock[]): void {
    this.cache.clear()
    content.forEach(block => {
      this.cache.set(block.key, block)
    })
    this.lastCacheUpdate = Date.now()
  }

  // Helper: Description des catégories
  private getCategoryDescription(category: string): string {
    const descriptions: Record<string, string> = {
      'hero': 'Section principale de la page d\'accueil',
      'features': 'Section des fonctionnalités et piliers',
      'stats': 'Statistiques et chiffres clés',
      'cta': 'Appels à l\'action',
      'navigation': 'Éléments de navigation',
      'footer': 'Pied de page',
      'members': 'Page des membres',
      'venues': 'Page des lieux',
      'events': 'Page des événements',
      'about': 'Page à propos',
      'contact': 'Page de contact'
    }
    return descriptions[category] || 'Contenu personnalisé'
  }

  // Initialiser le contenu par défaut (à exécuter une seule fois)
  async initializeDefaultContent(): Promise<void> {
    try {
      const defaultContent: Omit<ContentBlock, 'id' | 'lastUpdated' | 'updatedBy'>[] = [
        // Hero Section
        {
          key: 'hero_title',
          type: 'text',
          value: 'Bienvenue sur BreizhTeam Connections',
          defaultValue: 'Bienvenue sur BreizhTeam Connections',
          description: 'Titre principal de la page d\'accueil',
          category: 'hero',
          page: 'home',
          order: 1
        },
        {
          key: 'hero_subtitle',
          type: 'text',
          value: 'Là où le réseautage prend une nouvelle dimension',
          defaultValue: 'Là où le réseautage prend une nouvelle dimension',
          description: 'Sous-titre de la page d\'accueil',
          category: 'hero',
          page: 'home',
          order: 2
        },
        {
          key: 'hero_description',
          type: 'rich_text',
          value: 'Plongez au cœur de BreizhTeam Connections, votre plateforme dédiée à l\'expansion de votre univers professionnel dans l\'authentique esprit breton. Ici, nous cultivons l\'art de la rencontre, fusionnant tradition et modernité pour tisser des liens d\'affaires solides et durables.',
          defaultValue: 'Plongez au cœur de BreizhTeam Connections...',
          description: 'Description principale de la page d\'accueil',
          category: 'hero',
          page: 'home',
          order: 3
        },
        // Features Section
        {
          key: 'features_title',
          type: 'text',
          value: 'Notre Communauté',
          defaultValue: 'Notre Communauté',
          description: 'Titre de la section fonctionnalités',
          category: 'features',
          page: 'home',
          order: 1
        },
        {
          key: 'features_subtitle',
          type: 'text',
          value: 'Trois piliers pour un réseautage authentique et efficace',
          defaultValue: 'Trois piliers pour un réseautage authentique et efficace',
          description: 'Sous-titre de la section fonctionnalités',
          category: 'features',
          page: 'home',
          order: 2
        },
        // Members Feature
        {
          key: 'members_title',
          type: 'text',
          value: 'Les Membres',
          defaultValue: 'Les Membres',
          description: 'Titre de la section membres',
          category: 'features',
          page: 'home',
          order: 3
        },
        {
          key: 'members_description',
          type: 'rich_text',
          value: 'Rejoignez une communauté d\'esprits avant-gardistes : entrepreneurs, créateurs d\'innovation, artisans du changement. Ensemble, nous façonnons le futur des affaires en Bretagne et au-delà.',
          defaultValue: 'Rejoignez une communauté d\'esprits avant-gardistes...',
          description: 'Description de la section membres',
          category: 'features',
          page: 'home',
          order: 4
        },
        // Stats Section
        {
          key: 'stats_title',
          type: 'text',
          value: 'Notre Impact',
          defaultValue: 'Notre Impact',
          description: 'Titre de la section statistiques',
          category: 'stats',
          page: 'home',
          order: 1
        },
        {
          key: 'stats_members',
          type: 'number',
          value: '1250',
          defaultValue: '1250',
          description: 'Nombre de membres actifs',
          category: 'stats',
          page: 'home',
          order: 2
        },
        {
          key: 'stats_events',
          type: 'number',
          value: '180',
          defaultValue: '180',
          description: 'Nombre d\'événements organisés',
          category: 'stats',
          page: 'home',
          order: 3
        },
        {
          key: 'stats_connections',
          type: 'number',
          value: '5600',
          defaultValue: '5600',
          description: 'Nombre de connexions créées',
          category: 'stats',
          page: 'home',
          order: 4
        },
        {
          key: 'stats_countries',
          type: 'number',
          value: '25',
          defaultValue: '25',
          description: 'Nombre de pays représentés',
          category: 'stats',
          page: 'home',
          order: 5
        }
      ]

      for (const content of defaultContent) {
        await this.createContent(content)
      }

      console.log('✅ Contenu par défaut initialisé avec succès')
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation du contenu:', error)
    }
  }
}

export const contentService = new ContentService()